import { Button } from '@base-ui/react/button';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  type ContentSearchParams,
  parseContentSearch,
  searchQuery,
} from '@/admin/contentSearch';
import { apiErrorDetail } from '@/admin/errorDetail';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { QUESTION_TYPE_LABELS } from '@/admin/questionTypes';
import { announceSaved } from '@/admin/saveBanner';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { LessonHeader, TabHeading, TopicHeader } from '@/admin/ui/ContentNav';
import { SearchFilters } from '@/admin/ui/ContentSearch';
import { ErrorNotice } from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { finalQuizTitle } from '@/admin/ui/QuizBadge';
import { LockedQuizNotice } from '@/admin/ui/QuizLock';
import { Table } from '@/admin/ui/Table';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Question = components['schemas']['QuestionAdminSummary'];
type QuestionSearchItem = components['schemas']['QuestionSearchItem'];
type QuestionType = components['schemas']['QuestionTypes'];
type BulkAction = 'delete' | 'publish';

const PAGE_SIZE = 50;

export const Route = createFileRoute('/admin/questions/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { lesson?: number; topic?: number } & ContentSearchParams => ({
    // With a lesson, one quiz's questions; without, a search across every quiz.
    lesson: s.lesson != null ? Number(s.lesson) : undefined,
    // No topic means the lesson's final quiz.
    topic: s.topic != null ? Number(s.topic) : undefined,
    ...parseContentSearch(s, QUESTION_TYPES),
  }),
  component: QuestionsPage,
});

const QUESTION_TYPES = Object.keys(QUESTION_TYPE_LABELS) as QuestionType[];

// Worded like a single delete's prompt (`deletePrompt`), for a selection.
function bulkDeletePrompt(inDraft: Question[]): string {
  const copies = inDraft.filter((q) => q.lineage_id !== q.id).length;
  if (copies === 0) return i18n.t('admin.questions.bulkNoUndo');
  if (copies === inDraft.length)
    return i18n.t('admin.questions.bulkRestoreAll');
  return i18n.t('admin.questions.bulkRestoreSome');
}

function QuestionsPage() {
  const { lesson, topic } = Route.useSearch();
  return lesson !== undefined ? (
    <QuizQuestions lesson={lesson} topic={topic} />
  ) : (
    <QuestionSearch />
  );
}

const columnHelper = createColumnHelper<Question>();

const canDelete = (q: Question) => !q.locked;
const canPublish = (q: Question, quizLocked: boolean) =>
  q.status === 'draft' && !quizLocked;

type TableMeta = {
  quizLocked: boolean;
  selected: Set<number>;
  selectable: Question[];
  toggle: (id: number) => void;
  toggleAll: () => void;
};

const metaOf = (table: { options: { meta?: unknown } }) =>
  table.options.meta as TableMeta;

// Defined once: a cell renderer rebuilt per render would remount its checkbox
// on every tick, losing keyboard focus.
const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      const { selected, selectable, toggleAll } = metaOf(table);
      const all = selectable.length > 0 && selected.size === selectable.length;
      return (
        <SelectCell>
          <input
            type="checkbox"
            aria-label={i18n.t('admin.questions.selectAll')}
            checked={all}
            disabled={selectable.length === 0}
            ref={(el) => {
              if (el) el.indeterminate = selected.size > 0 && !all;
            }}
            onChange={toggleAll}
          />
        </SelectCell>
      );
    },
    cell: ({ row, table }) => {
      const { quizLocked, selected, toggle } = metaOf(table);
      const q = row.original;
      const selectable = canDelete(q) || canPublish(q, quizLocked);
      return (
        // Ticking a box shouldn't open the question.
        <SelectCell onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={i18n.t('admin.questions.selectOne', {
              id: q.id,
              interpolation: { escapeValue: false },
            })}
            checked={selected.has(q.id)}
            disabled={!selectable}
            title={
              selectable ? undefined : i18n.t('admin.questions.lockedNoDelete')
            }
            onChange={() => toggle(q.id)}
          />
          {q.locked && (
            <LockIcon
              icon={faLock}
              title={i18n.t('admin.questions.lockedWording')}
            />
          )}
        </SelectCell>
      );
    },
  }),
  columnHelper.accessor('order', {
    header: () => i18n.t('admin.common.order'),
  }),
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('type', {
    header: () => i18n.t('admin.questionEditor.typeLabel'),
    cell: (info) => i18n.t(QUESTION_TYPE_LABELS[info.getValue()]),
  }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.questionEditor.question'),
    // Drafts may be saved before their text is written.
    cell: (info) =>
      info.getValue() || i18n.t('admin.questionAnswer.noQuestionText'),
  }),
  columnHelper.accessor('score', {
    header: () => i18n.t('admin.questionEditor.points'),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
    // A draft in a locked quiz stays a draft; say so where it's listed.
    cell: (info) =>
      metaOf(info.table).quizLocked && info.getValue() === 'draft'
        ? i18n.t('admin.questions.draftCantPublish')
        : info.getValue(),
  }),
];

function QuizQuestions({ lesson, topic }: { lesson: number; topic?: number }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirming, setConfirming] = useState<BulkAction | null>(null);

  // Admin read: every status, so drafts can be finished and published.
  const { data, isLoading, error } = api.adminListQuestions.useQuery({
    path: { lesson_id: lesson },
    query: {
      ...(topic ? { topic_id: topic } : {}),
      limit: PAGE_SIZE,
      offset: PAGE_SIZE * page,
    },
  });

  // A topic quiz follows its topic; the final quiz follows the lesson.
  const lessonQuery = api.adminGetLesson.useQuery({
    path: { lesson_id: lesson },
  });
  const topicQuery = api.adminGetTopic.useQuery(
    { path: { topic_id: topic ?? 0 } },
    { enabled: topic !== undefined },
  );
  const parentData = topic !== undefined ? topicQuery.data : lessonQuery.data;
  const quizLocked = parentData?.quiz_locked ?? false;
  // In a draft, copies of live questions come back from Review changes; ones
  // written in the draft don't.
  const inDraft = parentData?.version_state === 'draft';

  const rows = data?.data ?? [];
  const selectable = rows.filter(
    (q) => canDelete(q) || canPublish(q, quizLocked),
  );
  const allSelected =
    selectable.length > 0 && selected.size === selectable.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectable.map((q) => q.id)));
  const chosen = rows.filter((q) => selected.has(q.id));
  const toDelete = chosen.filter(canDelete);
  const toPublish = chosen.filter((q) => canPublish(q, quizLocked));

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      quizLocked,
      selected,
      selectable,
      toggle: (id: number) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (!next.delete(id)) next.add(id);
          return next;
        }),
      toggleAll,
    } satisfies TableMeta,
  });

  const changePage = (next: number) => {
    setSelected(new Set());
    setPage(next);
  };

  // Deleting every question on the last page leaves it empty.
  useEffect(() => {
    if (data && data.data.length === 0 && page > 0) setPage(page - 1);
  }, [data, page]);

  const refreshQuiz = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminListQuestions.getQueryKey({
        path: { lesson_id: lesson },
      }),
    });
    // Their quiz counts feed the publish warnings.
    queryClient.invalidateQueries({
      queryKey: api.adminGetLesson.getQueryKey({
        path: { lesson_id: lesson },
      }),
    });
    if (topic !== undefined)
      queryClient.invalidateQueries({
        queryKey: api.adminGetTopic.getQueryKey({ path: { topic_id: topic } }),
      });
  };

  const afterBulk = (message: string) => {
    setSelected(new Set());
    setConfirming(null);
    announceSaved(message);
    refreshQuiz();
  };
  // A refusal can mean the rows changed under us (deleted, or now answered).
  const onBulkError = () => {
    setConfirming(null);
    refreshQuiz();
  };

  const deleteMutation = api.adminBulkDeleteQuestions.useMutation(undefined, {
    // It announces how many itself.
    meta: { toast: false },
    onSuccess: (_, variables) =>
      afterBulk(
        t('admin.questions.bulkDeleted', {
          count: variables.body.question_ids.length,
          interpolation: { escapeValue: false },
        }),
      ),
    onError: onBulkError,
  });
  const publishMutation = api.adminBulkPublishQuestions.useMutation(undefined, {
    meta: { toast: false },
    onSuccess: (_, variables) =>
      afterBulk(
        t('admin.questions.bulkPublished', {
          count: variables.body.question_ids.length,
          interpolation: { escapeValue: false },
        }),
      ),
    onError: onBulkError,
  });
  const busy = deleteMutation.isPending || publishMutation.isPending;
  const bulkError = deleteMutation.error ?? publishMutation.error;
  // `busy` lands a render late, so a fast double-click could send twice.
  const running = useRef(false);

  const runBulk = () => {
    if (running.current || confirming === null) return;
    running.current = true;
    const body = {
      topic_id: topic ?? null,
      question_ids: (confirming === 'delete' ? toDelete : toPublish).map(
        (q) => q.id,
      ),
    };
    deleteMutation.reset();
    publishMutation.reset();
    const mutation = confirming === 'delete' ? deleteMutation : publishMutation;
    mutation.mutate(
      { path: { lesson_id: lesson }, body },
      {
        onSettled: () => {
          running.current = false;
        },
      },
    );
  };

  if (isLoading) return <Message>{t('admin.questions.loading')}</Message>;
  if (error)
    return <Message $error>{t('admin.questions.errorLoading')}</Message>;

  const total = data?.count ?? 0;
  const parent = topic !== undefined ? 'topic' : 'lesson';
  const pagination = (
    <Pagination
      page={page}
      pageSize={PAGE_SIZE}
      shown={rows.length}
      total={total}
      onPage={changePage}
    />
  );
  const confirmList = confirming === 'delete' ? toDelete : toPublish;
  const notDeletable = chosen.length - toDelete.length;
  const notDrafts = chosen.length - toPublish.length;

  return (
    <div>
      {topic !== undefined
        ? topicQuery.data && <TopicHeader topic={topicQuery.data} />
        : lessonQuery.data && <LessonHeader lesson={lessonQuery.data} />}
      <TabHeading
        action={
          quizLocked ? (
            <Button className={buttonStyles.Button} disabled>
              {t('admin.questions.addQuestion')}
            </Button>
          ) : (
            <Link
              to="/admin/questions/new/"
              search={{ lesson, topic }}
              className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
            >
              {t('admin.questions.addQuestion')}
            </Link>
          )
        }
      >
        {topic !== undefined
          ? t('admin.quizBadge.topic')
          : finalQuizTitle(lessonQuery.data?.title)}
      </TabHeading>
      {quizLocked && <LockedQuizNotice parent={parent} />}

      <Status aria-live="polite">
        {bulkError && (
          <ErrorNotice role="alert">
            {apiErrorDetail(bulkError) ?? t('admin.questions.bulkError')}
          </ErrorNotice>
        )}
      </Status>

      {selected.size > 0 && (
        <BulkBar role="region" aria-label={t('admin.questions.selectedRegion')}>
          <BulkSelectAll>
            <input
              type="checkbox"
              aria-label={t('admin.questions.selectAll')}
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = !allSelected;
              }}
              onChange={toggleAll}
            />
          </BulkSelectAll>
          <strong>
            {t('admin.questions.nSelected', {
              n: selected.size,
              interpolation: { escapeValue: false },
            })}
          </strong>
          <BulkActions>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Primary}`}
              disabled={toPublish.length === 0 || busy}
              onClick={() => setConfirming('publish')}
            >
              {toPublish.length === 0
                ? t('admin.common.publish')
                : t('admin.questions.publishDrafts', {
                    count: toPublish.length,
                    interpolation: { escapeValue: false },
                  })}
            </Button>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Danger}`}
              disabled={toDelete.length === 0 || busy}
              onClick={() => setConfirming('delete')}
            >
              {toDelete.length === 0
                ? t('admin.common.delete')
                : t('admin.questions.deleteQuestions', {
                    count: toDelete.length,
                    interpolation: { escapeValue: false },
                  })}
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setSelected(new Set())}
            >
              {t('admin.questions.clear')}
            </Button>
          </BulkActions>
          {(notDeletable > 0 || notDrafts > 0) && (
            <BulkHint>
              {notDeletable > 0 &&
                `${t('admin.questions.lockedNotDeleted', { count: notDeletable, interpolation: { escapeValue: false } })} `}
              {notDrafts > 0 &&
                (quizLocked
                  ? t('admin.questions.draftsLocked')
                  : t('admin.questions.alreadyPublished', {
                      count: notDrafts,
                      interpolation: { escapeValue: false },
                    }))}
            </BulkHint>
          )}
        </BulkBar>
      )}

      {total > PAGE_SIZE && pagination}

      {total === 0 ? (
        <Message>
          {quizLocked
            ? t('admin.questions.noQuestions')
            : t('admin.questions.noQuestionsYet')}
        </Message>
      ) : (
        <Table
          table={table}
          to="/admin/questions/$questionId/"
          getParams={(row) => ({ questionId: String(row.id) })}
          isSelected={(row) => selected.has(row.id)}
        />
      )}
      {total > PAGE_SIZE && pagination}

      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming === 'delete'
            ? t('admin.questions.deleteTitle', {
                count: toDelete.length,
                interpolation: { escapeValue: false },
              })
            : t('admin.questions.publishTitle', {
                count: toPublish.length,
                interpolation: { escapeValue: false },
              })
        }
        confirmLabel={
          busy
            ? t('admin.questions.working')
            : confirming === 'delete'
              ? t('admin.questions.deleteQuestions', {
                  count: toDelete.length,
                  interpolation: { escapeValue: false },
                })
              : t('admin.questions.publishDrafts', {
                  count: toPublish.length,
                  interpolation: { escapeValue: false },
                })
        }
        cancelLabel={t('admin.common.cancel')}
        tone={confirming === 'delete' ? 'danger' : 'primary'}
        pending={busy}
        onConfirm={runBulk}
        onCancel={() => setConfirming(null)}
      >
        <ConfirmList>
          {confirmList.slice(0, 8).map((q) => (
            <li key={q.id}>
              #{q.id} {q.title || t('admin.questionAnswer.noQuestionText')}
            </li>
          ))}
          {confirmList.length > 8 && (
            <li>
              {t('admin.questions.andOthers', {
                count: confirmList.length - 8,
                interpolation: { escapeValue: false },
              })}
            </li>
          )}
        </ConfirmList>
        {confirming === 'delete' ? (
          <p>{bulkDeletePrompt(inDraft ? toDelete : [])}</p>
        ) : (
          <p>
            {parent === 'topic'
              ? t('admin.questions.publishNoteTopic')
              : t('admin.questions.publishNoteLesson')}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

const searchColumnHelper = createColumnHelper<QuestionSearchItem>();

const searchColumns = [
  searchColumnHelper.accessor('id', {
    header: () => i18n.t('admin.common.id'),
  }),
  searchColumnHelper.accessor('title', {
    header: () => i18n.t('admin.questionEditor.question'),
    cell: (info) =>
      info.getValue() || i18n.t('admin.questionAnswer.noQuestionText'),
  }),
  searchColumnHelper.accessor('type', {
    header: () => i18n.t('admin.questionEditor.typeLabel'),
    cell: (info) => i18n.t(QUESTION_TYPE_LABELS[info.getValue()]),
  }),
  searchColumnHelper.accessor('topic_title', {
    header: () => i18n.t('admin.questions.col.quiz'),
    cell: (info) =>
      info.getValue() ?? finalQuizTitle(info.row.original.lesson_title),
  }),
  searchColumnHelper.accessor('lesson_title', {
    header: () => i18n.t('admin.topics.lesson'),
  }),
  searchColumnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (info) => i18n.t(LANGUAGE_LABELS[info.getValue()]),
  }),
  searchColumnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
  }),
];

function QuestionSearch() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = (params.page ?? 1) - 1;

  const { data, isLoading, error } = api.adminSearchQuestions.useQuery({
    query: {
      ...searchQuery(params, LIST_PAGE_SIZE),
      ...(params.type ? { type: params.type } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const update = (patch: ContentSearchParams, replace = true) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={(next) =>
        update({ page: next > 0 ? next + 1 : undefined }, false)
      }
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <PageTitle>{t('admin.nav.questions')}</PageTitle>
      <Intro>{t('admin.questions.intro')}</Intro>
      <SearchFilters
        params={params}
        onChange={update}
        placeholder={t('admin.questions.searchPlaceholder')}
        typeLabels={QUESTION_TYPE_LABELS}
      />
      {isLoading ? (
        <Message>{t('admin.questions.loading')}</Message>
      ) : error ? (
        <Message $error>{t('admin.questions.errorLoading')}</Message>
      ) : data?.count === 0 ? (
        <Message>{t('admin.questions.noMatch')}</Message>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/admin/questions/$questionId/"
            getParams={(row) => ({ questionId: String(row.id) })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

const Intro = styled.p`
  color: var(--grey-700);
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
`;

const Status = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

// Stays in view while scrolling a long page of ticked rows.
const BulkBar = styled.div`
  align-items: center;
  background: var(--component-color);
  border: 1px solid var(--blue);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  position: sticky;
  top: 0.5rem;
  z-index: 5;
`;

const BulkSelectAll = styled.label`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  margin: -0.5rem 0 -0.5rem -0.25rem;
  padding: 0.5rem;

  input {
    cursor: pointer;
    height: 1rem;
    width: 1rem;
  }
`;

const BulkActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

const BulkHint = styled.p`
  color: var(--grey-700);
  flex-basis: 100%;
  font-size: 0.8125rem;
  margin: 0;
`;

const SelectCell = styled.label`
  align-items: center;
  cursor: default;
  display: inline-flex;
  gap: 0.4rem;
  margin: -0.75rem;
  padding: 0.75rem;

  input {
    cursor: pointer;
    height: 1rem;
    width: 1rem;
  }

  input:disabled {
    cursor: not-allowed;
  }
`;

const LockIcon = styled(FontAwesomeIcon)`
  color: var(--grey-550);
  font-size: 0.75rem;
`;

const ConfirmList = styled.ul`
  max-height: 12rem;
  overflow-y: auto;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
